'use client';
import useSWR from 'swr';

import { defaultFetcher, FSLocale, localeLink } from '@fshr/shared';

import { StaysSection } from './StaysSection';
import { ReturnAllStaysSection } from './ReturnAllStaysSection';
import LoadingIndicator from '../loading-indicator';
import ErrorAlert from '../error-alert';
import { QuickLinksSection } from './QuickLinksSection';
import { OutletsInfoType, ReserveTableSection } from './ReserveTableSection';
import { ItineraryComponentProps } from './ItineraryComponent';
import { RecommendationsSection } from './RecommendationSection';
import { Timeline } from '../timeline/timeline';
import { getConfig } from '@fshr/shared-react';
import { useTealium } from '@fshr/context/tealium-context';
import { useEffect, useState, useCallback } from 'react';
import EmptyItinerary from './EmptyItineraryComponent';
import { observer } from 'mobx-react-lite';
import { AncilliariesRecommendations } from '../types';
import { useQualtricsIntercept } from '@fshr/hooks';
import { getItineraryQualtricsEmbeddedData } from '@fshr/shared-react';

type DiningResponseType = {
	outlets: OutletsInfoType[];
	diningLandingUrl?: string;
};

export type QuickLinkData = {
	url: string;
	label: string;
	icon: string;
};

type ItineraryBffError = {
	status: string | number;
	ojCode?: string;
	message?: string;
	redirectUrl?: string;
};
type WebchatData = {
	enabled: boolean;
};

export type ResponseDataType = {
	propertyAnalytics: PropertyAnalytics;
	bookingSummary: BookingData;
	diningOutlets?: DiningResponseType;
	recommendations: Recommendation[];
	quickLinks: QuickLinkData[];
	timelineItems: TimelineItem[];
	itinerary: ItineraryData;
	webchat: WebchatData;
	error?: ItineraryBffError;
	ancilliariesRecommendations?: AncilliariesRecommendations;
};

export interface TimelineItem {
	date: string;
	item: {
		guestVisibleNotes: string | undefined;
		totalGuests: string | undefined;
		checkInTime: string | undefined;
		dailyRateDisclaimer: string | undefined;
		type: string;
		status: string;
		vendor?: string;
		vendorNameOnItinerary?: string;
		productName: string;
		roomType?: { name: string };
		offer?: string;
		averageDailyRate?: string;
		totalRoomRate?: string;
		estimatedTotal?: string;
		disclaimer?: string;
		currencyCode?: string;
		needsTransportation?: boolean;
		checkOutTime?: string;
		notes?: string;
		requestType?: string;
		requestSubtype?: string;
		totalCost?: string;
		vehicleSelected?: string;
		pickupLocation?: string;
		dropoffLocation?: string;
		guests?: string;
		numberOfGuests?: string;
		quantity?: number;
		description?: string;
		duration?: string;
		confirmationNumber?: string;
		departureLocation?: string;
		time?: string;
		cancellationPolicy?: string;
		preferredTimeOfDay?: string;
		numberOfItems?: number;
	};
}

export type BookingData = {
	propertyName: string;
	checkInDate: string;
	checkOutDate: string;
	confirmationNumber: string;
	roomType?: {
		roomTypeId?: string;
		name?: string;
		fsRoomToCharge?: string;
	};
	propertyOwsCode: string;
};

export type PropertyAnalytics = {
	id?: string;
	enCountry: string;
	enRegion: string;
	orsCode: string;
};

export type Recommendation = {
	name: string;
	imageUrl: string;
	imageAlt: string;
	url: string;
	propertyOrsCode?: string;
	sku?: string;
	datalakeRecoId?: string;
};

export type ItineraryData = {
	propertyOrsCode: string;
	days: Array<{
		date: string;
		items: Array<{
			requestType: string;
			requestSubtype: string;
			numberOfGuests: number;
			numberOfAdults?: number;
			status: string;
			duration?: string;
			currencyCode: string;
			totalCost: number;
			vendorNameOnItinerary?: string;
			preferredTimeOfDay?: string;
			cancellationPolicy?: string;
			description?: string;
			isTransportation: boolean;
			guestVisibleNotes?: string;
			vehicleSelected?: string;
			numberOfItems?: number;
			type?: string;
		}>;
	}>;
	hasRequestedArrivalTransportation: boolean;
	hasRequestedDepartureTransportation: boolean;
	hasRequestedWelcomeAmenities: boolean;
	requestDepartureTransportationUrl: string;
	requestArrivalTransportationUrl: string;
};

/**
 * Function used to build the URL used to fetch information from itinerary route
 *
 * @param { string | undefined } param0.activeBookingId
 * @param { string } param0.local
 * @param { string | null } param0.propertyOwsCode
 * @returns { string } Itinerary route
 */
const getUrl = ({
	activeBookingId,
	locale,
	propertyOwsCode,
	currencyCode,
}: {
	activeBookingId?: string;
	locale: string;
	propertyOwsCode?: string | null;
	currencyCode?: string;
}) => {
	const { NEXT_PUBLIC_BFF_BASE_URL } = getConfig();

	const queryString = new URLSearchParams();

	if (activeBookingId) {
		queryString.set('bookingId', activeBookingId);
	}

	if (propertyOwsCode) {
		queryString.set('propertyOwsCode', propertyOwsCode);
	}

	if (currencyCode) {
		queryString.set('currencyCode', currencyCode);
	}

	return localeLink(locale as FSLocale)(`${NEXT_PUBLIC_BFF_BASE_URL}/itinerary/?${queryString}`);
};

export const ItineraryBody = observer((props: ItineraryComponentProps) => {
	const { globalStore, locale, closeComponent, urlPath = '' } = props;
	const { trackLink } = useTealium();
	const isFullyAuthenticated = globalStore?.profile?.dataRedacted === false;

	const config = getConfig();
	const interceptId = config['FSHR_PUBLIC_ITINERARY_SURVEY_INTERCEPT_ID'] as string | undefined;
	const surveyLink = config['FSHR_PUBLIC_ITINERARY_SURVEY_LINK'] as string | undefined;
	const environmentTag = (
		config['FSHR_PUBLIC_TEALIUM_ENVIRONMENT'] as string | undefined
	)?.toLowerCase();
	const stageHostname = typeof window !== 'undefined' ? window.location.hostname : '';
	const isStageHost = /(^|\.)((dev|qa)\.stage)\./.test(stageHostname);
	const isProductionEnvironment = environmentTag
		? environmentTag === 'prod' && !isStageHost
		: process.env['NODE_ENV'] === 'production' && !isStageHost;
	const surveyEnabled = Boolean(interceptId && surveyLink);
	const surveyLinkPresent = Boolean(surveyLink);
	//Qualtrics intercept
	const qualtricsIntercept = useQualtricsIntercept(interceptId, surveyEnabled);
	const interceptPassed = Boolean(qualtricsIntercept?.passed);
	const forceSurvey =
		!isProductionEnvironment &&
		typeof window !== 'undefined' &&
		new URLSearchParams(window.location.search).has('force-itinerary-survey');
	//Track if survey was dismissed/interacted
	const [surveyInteracted, setSurveyInteracted] = useState(false);

	const { data, isLoading, error } = useSWR<ResponseDataType>(
		getUrl({
			propertyOwsCode: globalStore?.currentProperty,
			activeBookingId: globalStore?.activeBookingId,
			locale: locale ?? 'en',
			currencyCode: globalStore?.preferredCurrency,
		}),
		defaultFetcher,
		{
			revalidateIfStale: false,
			revalidateOnFocus: false,
			revalidateOnReconnect: false,
			errorRetryCount: 0,
		}
	);

	const {
		propertyAnalytics,
		bookingSummary,
		diningOutlets,
		recommendations,
		quickLinks,
		error: apiError,
	} = data ?? {};

	const openedOnMyStayPage = /^(\/|)(\w{1,2}\/|)profile\/my-stays(\/|)$/.test(urlPath);

	//Create survey link with embedded data
	const createSurveyLink = useCallback(
		(starRating: number) => {
			if (!surveyLink) return '';

			const embeddedData = getItineraryQualtricsEmbeddedData({
				bookingSummary,
				propertyAnalytics,
				profile: globalStore?.profile,
				urlPath: urlPath || '',
				itineraryData: data?.itinerary,
				userHasItinerary: Boolean(globalStore?.userHasItinerary),
				interceptId: interceptId || '',
				environmentTag: environmentTag || 'production',
				sessionData: {
					sessionStartTime: (globalStore as any)?.sessionStartTime,
					visitedPages: (globalStore as any)?.visitedPages || [],
					searchTerm: '',
				},
			});
			const payload = {
				...embeddedData,
				StarRating: starRating,
			};

			if (qualtricsIntercept?.passed) {
				return qualtricsIntercept.createSurveyLink(surveyLink, payload);
			}

			//Fallback only for force mode in non-production
			if (forceSurvey && !isProductionEnvironment) {
				try {
					const url = new URL(surveyLink);
					Object.entries(payload).forEach(([key, value]) => {
						if (value === undefined || value === null) {
							return;
						}

						url.searchParams.set(key, String(value));
					});

					return url.toString();
				} catch (error) {
					console.warn('Failed to build itinerary survey link fallback', error);
					return '';
				}
			}

			return '';
		},
		[
			qualtricsIntercept,
			surveyLink,
			bookingSummary,
			propertyAnalytics,
			globalStore,
			urlPath,
			data?.itinerary,
			forceSurvey,
			isProductionEnvironment,
		]
	);

	const handleSurveyInteraction = useCallback(() => {
		setSurveyInteracted(true);
	}, []);

	const handleSurveyDismiss = useCallback(() => {
		setSurveyInteracted(true);
	}, []);

	const shouldRenderSurvey =
		surveyEnabled && !surveyInteracted && interceptPassed && Boolean(bookingSummary);

	//Allow force mode on hotel discover pages even if booking summary is absent
	const hotelDiscoverRegex = /\/[^/]+\/discover\/?$/;
	const rootDiscoverRegex = /^(\/|)(\w{1,2}\/|)discover\/?$/;
	const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
	const isHotelDiscoverPage =
		hotelDiscoverRegex.test(pathname) && !rootDiscoverRegex.test(pathname);

	const forceModeBookingSatisfied =
		Boolean(bookingSummary) || (isHotelDiscoverPage && !bookingSummary);
	const showSurvey = forceSurvey
		? surveyLinkPresent && !surveyInteracted && forceModeBookingSatisfied
		: shouldRenderSurvey;

	//Debug logging in non-prod - '/?force-itinerary-survey=1'
	useEffect(() => {
		if (typeof window === 'undefined') return;
		const hasForceParam = new URLSearchParams(window.location.search).has('force-itinerary-survey');
		if (hasForceParam) {
			console.log('[Itinerary Survey][Force Parameter Detected]', {
				environmentTag,
				isProductionEnvironment,
				forceSurvey,
				pathname: window.location.pathname,
				search: window.location.search,
			});
		}
	}, [environmentTag, isProductionEnvironment, forceSurvey]);

	useEffect(() => {
		if (!globalStore?.userPanelOpenTab || !bookingSummary) return;
		const getUpcomingStay = () => {
			if (!bookingSummary?.checkInDate) return 'no upcoming stay';

			const today = new Date();
			const checkInDate = new Date(bookingSummary.checkInDate);
			const checkOutDate = new Date(bookingSummary.checkOutDate);

			// If currently in stay
			if (today >= checkInDate && today <= checkOutDate) {
				return 'in stay';
			}

			// Calculate days until stay
			const diffTime = checkInDate.getTime() - today.getTime();
			const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
			return diffDays.toString();
		};

		trackLink('itinerary_overlay_view', {
			page_detail: 'itinerary',
			property_name: propertyAnalytics?.id,
			property_code: propertyAnalytics?.orsCode,
			upcoming_stay: getUpcomingStay(),
			property_region: propertyAnalytics?.enRegion,
			property_subregion: propertyAnalytics?.enCountry,
			golden_id: globalStore.profile?.goldenId,
		});
	}, [globalStore?.userPanelOpenTab, bookingSummary]);

	// Show loading state while fetching data
	if (isLoading || globalStore?.fetching) {
		return <LoadingIndicator />;
	}

	// Check for errors after data has been loaded
	if (globalStore?.error || error) {
		return (
			<main className="w-full" aria-label="Itinerary Body">
				<ErrorAlert />
			</main>
		);
	}

	// Show empty itinerary when data is loaded but no booking exists
	if (apiError) {
		return (
			<main className="w-full" aria-label="Itinerary Body">
				<EmptyItinerary bookNowUrl={apiError.redirectUrl} />
			</main>
		);
	}

	// Show main itinerary content when we have booking data
	return (
		<main className="w-full" aria-label="Itinerary Body">
			{!isFullyAuthenticated && (
				<ReturnAllStaysSection
					closeComponent={closeComponent}
					redirect={!openedOnMyStayPage}
					locale={locale}
				/>
			)}
			{bookingSummary ? <StaysSection bookingData={bookingSummary} /> : null}
			{bookingSummary && quickLinks?.length ? (
				<QuickLinksSection
					quickLinks={quickLinks}
					locale={(locale as FSLocale) ?? 'en'}
					closeComponent={closeComponent}
					urlPath={urlPath}
					linkClassName="border-[1px]"
				/>
			) : null}
			{globalStore?.featureFlags.enableRecommendationEngine ? (
				<RecommendationsSection
					propertyId={propertyAnalytics?.id}
					recommendations={recommendations ?? null}
					ancilliariesRecommendations={data?.ancilliariesRecommendations?.recommendations}
					placement="itinerary-recommended_experiences"
				/>
			) : null}

			<Timeline
				bookingId={globalStore?.activeBookingId ?? ''}
				locale={locale}
				data={{
					// Create synthetic arrival item when timeline items are missing but booking exists
					timelineItems: data?.timelineItems?.length
						? data.timelineItems
						: bookingSummary
							? [
									{
										date: bookingSummary.checkInDate,
										item: {
											type: 'arrival',
											status: 'confirmed',
											checkInTime: undefined,
											roomType: bookingSummary.roomType?.name
												? { name: bookingSummary.roomType.name }
												: undefined,
											vendorNameOnItinerary: bookingSummary.propertyName,
											productName: bookingSummary.propertyName,
											guestVisibleNotes: undefined,
											totalGuests: undefined,
											dailyRateDisclaimer: undefined,
										},
									},
								]
							: [],
					itinerary: {
						hasRequestedDepartureTransportation:
							data?.itinerary?.hasRequestedDepartureTransportation ?? false,
						requestDepartureTransportationUrl:
							data?.itinerary?.requestDepartureTransportationUrl ?? '',
						hasRequestedArrivalTransportation:
							data?.itinerary?.hasRequestedArrivalTransportation ?? false,
						requestArrivalTransportationUrl: data?.itinerary?.requestArrivalTransportationUrl ?? '',
					},
					booking: bookingSummary ? bookingSummary : undefined,
					webchat: {
						enabled: Boolean(globalStore?.webChatEnabled),
					},
				}}
				survey={
					showSurvey
						? {
								enabled: true,
								createSurveyLink,
								onSurveyInteraction: handleSurveyInteraction,
								onDismiss: handleSurveyDismiss,
							}
						: undefined
				}
			/>
			{diningOutlets && diningOutlets?.outlets?.length ? (
				<ReserveTableSection
					outlets={diningOutlets.outlets}
					landingUrl={diningOutlets.diningLandingUrl}
				/>
			) : null}
		</main>
	);
});
